import { Injectable, inject } from '@angular/core';
import { NgKLoggerService } from '@kin/ngk-logger';
import { WINDOW } from '@ng-web-apis/common';

import { AppConfigService } from 'app/app-config';
import { PolicyStore } from 'app/common/services/legacy/policies/policies.store';
import { QuoteDraftStore } from 'app/global-store/quote-draft';
import { UserStore } from 'app/global-store/user/user.store';

declare global {
  interface Window {
    dataLayer: unknown[];
  }
}

export enum SegmentEvent {
  USER_LOGIN = 'user_login',
  QUOTE_COMPARE_VIEWED = 'self_serve_quote_compare_seen',
  QUOTE_COMPARE_SUBMITTED = 'self_serve_quote_compare_submitted',
  QUOTE_VIEWED = 'self_serve_quote_seen',
  QUOTE_MODIFIED = 'self_serve_quote_modified',
  QUOTE_SUBMITTED = 'self_serve_quote_submitted',
  CO_APPLICANT_PAGE_VIEWED = 'self_serve_coapplicant_page_seen',
  CO_APPLICANT_PAGE_MODIFIED = 'self_serve_coapplicant_page_modified',
  CO_APPLICANT_PAGE_SUBMITTED = 'self_serve_coapplicant_page_submitted',
  INSURANCE_INFO_PAGE_VIEWED = 'self_serve_insurance_info_page_seen',
  INSURANCE_INFO_PAGE_SUBMITTED = 'self_serve_insurance_info_page_submitted',
}

/**
 * Google Tag Manager Service (GTM Service)
 *   - manages GTM initialization and dataLayer events
 *   - GTM provided source from the container:
 *       https://tagmanager.google.com/#/container/accounts/1491202156/containers/7030390/workspaces/354/tags
 *   - @todo abstract this to something that can be used across apps, see QT-311
 */

@Injectable({
  providedIn: 'root',
})
export class GoogleTagManagerService {
  private gtmContainerId = 'GTM-TH4CKLW'; // This is currently the only GTM container in use
  private window = inject(WINDOW);
  private logger = inject(NgKLoggerService);
  private _appConfig = inject(AppConfigService);
  private userStore = inject(UserStore);
  private policyStore = inject(PolicyStore);
  private quoteDraftStore = inject(QuoteDraftStore);
  private document = this.window.document;
  private isNotProd = true; // Defaults to true, but will be set in init(), appContext is not available when GTM service is instantiated
  private hasGtmScript = false;
  private segmentUserLoaded = false;

  /**
   * Init the dataLayer and push the start event
   *   - if GTM fails to load (or actively blocked), define a global
   *       `dataLayer` array to fallback and fail gracefully
   */

  // GTM source:
  //     w[l] = w[l] || [];
  //     w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
  private initDataLayer() {
    this.window.dataLayer = this.window.dataLayer || [];
    this.window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    });
  }

  public init() {
    this.isNotProd = this._appConfig.appContext?.notProd ?? true;

    /**
     * Don't load prod GTM container in non-production environments
     */
    if (this.isNotProd) {
      this.gtmContainerId = 'TESTING';
    }

    /**
     * Don't load multiple instances of the GTM script
     */
    this.hasGtmScript = !!this.document.getElementById('gtmQuoteContainer');
    if (this.hasGtmScript) {
      return;
    }

    // GTM source:
    //     var f = d.getElementsByTagName(s)[0],
    const firstScriptElement = this.document.getElementsByTagName('script')[0]; // This is how the GTM snippet does it, and if we run into issues we can insert from <head> with something like `headElt.insertBefore(gtmScript, headElt.children[0])`

    // GTM source:
    //       j = d.createElement(s),
    const gtmScript = document.createElement('script');

    // GTM source:
    //     j.async = true;
    gtmScript.async = true;

    // GTM source:
    // (not sure why there is a self comparison here when 'dataLayer' is hardcoded in source)
    //       dl = l != 'dataLayer' ? '&l=' + l : '';
    //     j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
    gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${this.gtmContainerId}&l=dataLayer`;
    gtmScript.id = 'gtmQuoteContainer';

    /**
     * When there is something wrong with DOM script creation, so if NgKLogger is available we should capture it
     */
    if (!firstScriptElement.parentNode) {
      this.logger.error({ message: 'Could not create gtm.js script element because parentNode is missing', context: 'Google Tag Manager Init', priority: 'P4' });
      return;
    }

    /**
     * Google says to, "Paste this code as high in the <head> of the page as possible"
     *   - GTM source uses the first <script> element, and if we run into issues we can load from <head>
     *       with something like `headElt.insertBefore(testScript, headElt.children[0])`
     */
    // GTM source:
    //     f.parentNode.insertBefore(j, f);
    firstScriptElement.parentNode.insertBefore(gtmScript, firstScriptElement);
    this.initDataLayer();
  }

  /**
   * Getter for dataLayer
   */
  public getDataLayer(): unknown[] {
    return this.window.dataLayer;
  }

  /**
   * Setter dataLayer event (appends)
   */
  public sendDataLayerEvent(event: string, payload: Record<string, unknown> = {}) {
    this.pushOnDataLayer({
      event,
      data: {
        kin_segment_user_id: this.userStore.kinSegmentId(),
        kin_id: this.userStore.kinId(),
        kin_person_id: this.userStore.kinPersonId(),
        variant: 'v1', // So we know the tracking event comes from the new quoting-ui funnel
        ...payload,
      },
    });
  }

  /**
   * Setter for kin_id
   * - this is the user identifier for Segment, id from the users table
   */
  public setKinId(kin_id: number) {
    if (this.userStore.kinId() === kin_id) {
      return;
    }

    // Log event if overwriting kin_id
    if (this.userStore.kinId() !== undefined) {
      this.logger.event({
        name: 'GTMService::SetKinId',
        metaData: {
          message: 'The kin_id was already set, and has been overwritten.',
        },
      });
    }

    this.userStore.setKinId(kin_id);
    this.sendDataLayerUserLoginEvent();
  }

  /**
   * Setter for kin_person_id
   *   - this is the user identifier for Segment, id from the people table
   */
  public setKinPersonId(kin_person_id: number) {
    if (this.userStore.kinPersonId() === kin_person_id) {
      return;
    }
    // Log event if overwriting kin_person_id
    if (this.userStore.kinPersonId() !== undefined) {
      this.logger.event({
        name: 'GTMService::SetKinPersonId',
        metaData: {
          message: 'The kin_person_id was already set, and has been overwritten.',
        },
      });
    }
    this.userStore.setKinPersonId(kin_person_id);
  }

  /**
   * Setter for kin_segment_user_id
   * - this is the user identifier for Segment, composite identifier using id from the people table, "production-xxxxxx" format
   */
  public setKinSegmentId(kin_segment_user_id: string) {
    if (this.userStore.kinSegmentId() === kin_segment_user_id) {
      return;
    }

    // Log event if overwriting kin_segment_user_id
    if (this.userStore.kinSegmentId() !== '') {
      this.logger.event({
        name: 'GTMService::SetKinSegmentId',
        metaData: {
          message: 'The kin_segment_user_id was already set, and has been overwritten.',
        },
      });
    }

    this.userStore.setKinSegmentId(kin_segment_user_id);
    this.sendDataLayerUserLoginEvent();
  }

  public sendDataLayerEventWithQuoteData(event: string, payload: Record<string, unknown> = {}) {
    this.pushOnDataLayer({
      event,
      data: {
        kin_segment_user_id: this.userStore.kinSegmentId(),
        kin_id: this.userStore.kinId(),
        kin_person_id: this.userStore.kinPersonId(),
        variant: 'v1', // So we know the tracking event comes from the new quoting-ui funnel
        ...this.policyStore.coverageData(),
        ...this.quoteDraftStore.quoteData(),
        ...payload,
      },
    });
  }

  public sendDataLayerUserLoginEvent() {
    if (this.segmentUserLoaded) {
      return;
    }

    // Only send the user login event if the kin_segment_user_id and kin_id are set
    if (this.userStore.kinId() !== undefined && this.userStore.kinSegmentId() !== '' && this.userStore.kinSegmentId() !== undefined) {
      this.pushOnDataLayer({
        event: SegmentEvent.USER_LOGIN,
        kin_segment_user_id: this.userStore.kinSegmentId(),
        kin_id: this.userStore.kinId(),
        kin_person_id: this.userStore.kinPersonId(),
      });

      this.segmentUserLoaded = true;
    }
  }

  private pushOnDataLayer(obj: Record<string, unknown>) {
    const dataLayer = this.getDataLayer() || []; // Fail gracefully when dataLayer is missing
    dataLayer.push(obj);
  }
}
